import React from 'react';
import { inject, observer } from 'mobx-react';
import styles from './index.module.scss';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import { withRouter } from 'react-router-dom';
import { BaseComponent } from '@components/Base/h5/BaseComponent';
import type { BaseRouteComponentProps } from '../../../types/base';
import dayjs from 'dayjs';
import classNames from 'classnames';

const duration = require('dayjs/plugin/duration');
// 启用 duration 插件
dayjs.extend(duration);

interface H5CountDownProps extends BaseRouteComponentProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    number?: number; // 结束时间戳
    showDays?: boolean; // 是否显示天数
    timeSeparator?: string; // 时分秒分隔符，不传时显示中文单位
    isShow?: boolean; // 是否显示"剩余时间:"文字
    endText?: string; // 结束后的文字
    start?: boolean; // 是否显示"后开始"
    boxStylesData?: any // 最外层父级盒子自定义样式
    itemStylesData?: any    // 单个item的自定义样式
    endTextStylesData?: React.CSSProperties; // 结束文案的自定义行内样式
    onEnd?: () => void;
}
interface H5CountDownState {
    init: any,
    number: number,
    showTime: boolean,  // 显示倒计时
    days: any,
    hours: any,
    minutes: any,
    seconds: any,
    
}

@inject('app', 'site', 'user')
@observer
class H5CountDown extends React.Component<H5CountDownProps, H5CountDownState> {
    interval: NodeJS.Timeout | null = null;
    hasEnded = false;
    state: H5CountDownState = {
        init: false,
        number: 0,
        showTime: false,  // 显示倒计时
        days: '00',
        hours: '00',
        minutes: '00',
        seconds: '00'
    };

    componentDidMount() {
        let { number } = this.props;
        this.setState({ number: number || 0 }, () => {
            this.interval && clearInterval(this.interval);
            this._startCountDownTimer();
            this.interval = setInterval(this._startCountDownTimer, 1000);
        });
    }

    componentDidUpdate(prevProps: H5CountDownProps) {
        if (prevProps.number !== this.props.number) {
            this.hasEnded = false;
            this.setState({ number: this.props.number || 0 }, () => {
                this.interval && clearInterval(this.interval);
                this._startCountDownTimer();
                this.interval = setInterval(this._startCountDownTimer, 1000);
            });
        }
    }

    componentWillUnmount() {
        // 结束倒计时
        this._stopCountDownTimer();
    }

    _stopCountDownTimer = () => {
        this.interval && clearInterval(this.interval);
    };

    _formatNumber = (number: number) => {
        number = parseInt(String(number));
        if (number < 10) return '0' + number;
        return String(number);
    };

    _startCountDownTimer = () => {
        const { number } = this.state;
        const now = dayjs();
        const endTime = dayjs(number);
        const diff = endTime.diff(now);

        if (diff <= 0) {
            this._stopCountDownTimer();
            this.setState({
                showTime: false
            });
            if (!this.hasEnded) {
                this.hasEnded = true;
                this.props.onEnd?.();
            }
            return;
        }

        // 计算总天数
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        this.setState({
            days: this._formatNumber(days),
            hours: this._formatNumber(hours),
            minutes: this._formatNumber(minutes),
            seconds: this._formatNumber(seconds),
            showTime: true
        });
    };

    renderContent() {
        const { days, hours, minutes, seconds, showTime } = this.state;
        const {
            isShow,
            endText,
            start,
            showDays = true,
            timeSeparator,
            boxStylesData,
            itemStylesData,
            endTextStylesData,
        } = this.props;

        if (!showTime) return null;

        return (
            <div className={classNames(styles.countDownBox)} style={boxStylesData}>
                {isShow ? '剩余时间:' : ''}
                {showDays && <><div className={styles.countDownItem} style={itemStylesData}>{days}</div>天</>}
                {/* 自定义分隔符只影响时分秒，默认保留中文单位。 */}
                <div className={styles.countDownItem} style={itemStylesData}>{hours}</div>{timeSeparator ?? '时'}
                <div className={styles.countDownItem} style={itemStylesData}>{minutes}</div>{timeSeparator ?? '分'}
                <div className={styles.countDownItem} style={itemStylesData}>{seconds}</div>{timeSeparator === undefined ? '秒' : ''}
                {endText !== '' && endText ? (
                    <div style={endTextStylesData}>{endText}</div>
                ) : (
                    !isShow && start ? '后开始' : '后结束'
                )}
            </div>
        );
    }

    render() {
        return this.renderContent();
    }
}

export default withRouter(H5CountDown);
